import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { api, get, communityUrl, money, STATUS, RENTAL_OPTIONS } from "@/lib/api";

const sel = "w-full rounded-md border border-input bg-white px-3 py-2 text-sm h-9";
const FIELDS = ["name", "monthly_fee", "fee_range", "fee_frequency", "pet_policy", "rental_notes", "phone", "website"];

function EditDialog({ c, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...Object.fromEntries(FIELDS.map((k) => [k, c[k] ?? ""])), status: c.status, rental_policy: c.rental_policy || "", pets_allowed: c.pets_allowed == null ? "" : String(c.pets_allowed) }));
  const save = async () => {
    const body = { ...f, monthly_fee: f.monthly_fee === "" ? null : Number(f.monthly_fee), pets_allowed: f.pets_allowed === "" ? null : f.pets_allowed === "true" };
    try { await api.put(`/admin/communities/${c.id}`, body); toast.success("Community updated"); onSaved(); }
    catch { toast.error("Update failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-edit-dialog">
        <DialogHeader><DialogTitle>Edit {c.name}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label htmlFor="e-status">Status</Label>
            <select id="e-status" className={sel} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="admin-edit-status">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select></div>
          <div className="space-y-1.5"><Label htmlFor="e-pets">Pets allowed</Label>
            <select id="e-pets" className={sel} value={f.pets_allowed} onChange={(e) => setF({ ...f, pets_allowed: e.target.value })} data-testid="admin-edit-pets"><option value="">Not reported</option><option value="true">Yes</option><option value="false">No</option></select></div>
          <div className="space-y-1.5"><Label htmlFor="e-rent">Rental policy</Label>
            <select id="e-rent" className={sel} value={f.rental_policy} onChange={(e) => setF({ ...f, rental_policy: e.target.value })} data-testid="admin-edit-rental"><option value="unknown">Not reported</option>{RENTAL_OPTIONS.filter((o) => o.value !== "allows_rentals").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          {FIELDS.map((k) => (
            <div key={k} className="space-y-1.5"><Label htmlFor={`e-${k}`} className="capitalize">{k.replace(/_/g, " ")}</Label>
              <Input id={`e-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`admin-edit-${k}`} /></div>
          ))}
        </div>
        <button onClick={save} className="mt-4 rounded-full bg-[#0B2150] text-white px-6 py-2.5 font-semibold hover:bg-[#03A896]" data-testid="admin-edit-save">Save changes</button>
      </DialogContent>
    </Dialog>
  );
}

export const AdminCommunities = () => {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const key = ["admin-communities", q, status, page];
  const { data } = useQuery({ queryKey: key, queryFn: () => get("/admin/communities", { q: q || undefined, status: status || undefined, page }) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-communities"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };
  const del = async (c) => { if (window.confirm(`Delete ${c.name}?`)) { await api.delete(`/admin/communities/${c.id}`); toast.success("Deleted"); refresh(); } };

  return (
    <div data-testid="admin-communities">
      <div className="flex flex-wrap gap-2 mb-4">
        <Input placeholder="Search name or city" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="max-w-xs bg-white" data-testid="admin-communities-search" />
        <select className={`${sel} max-w-[200px]`} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="admin-communities-status">
          <option value="">All statuses</option>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span className="self-center text-sm text-slate-600">{data?.total?.toLocaleString() ?? 0} total</span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[#E2E8F0] bg-white">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#F8FAFC] text-left text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="p-3">Community</th><th className="p-3">Location</th><th className="p-3">Fee</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {data?.items?.map((c) => (
              <tr key={c.id} className="border-t" data-testid={`admin-community-row-${c.id}`}>
                <td className="p-3"><Link to={communityUrl(c)} className="font-medium text-[#0B2150] hover:underline">{c.name}</Link></td>
                <td className="p-3">{c.city}, {c.state_code}</td>
                <td className="p-3">{c.fee_range || money(c.monthly_fee)}</td>
                <td className="p-3"><StatusBadge status={c.status} /></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEdit(c)} aria-label={`Edit ${c.name}`} className="p-2 rounded-full hover:bg-slate-100" data-testid={`admin-community-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(c)} aria-label={`Delete ${c.name}`} className="p-2 rounded-full hover:bg-red-50 text-red-700" data-testid={`admin-community-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2 mt-4">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border px-4 py-1.5 text-sm disabled:opacity-40" data-testid="admin-communities-prev">Previous</button>
        <button disabled={!data || page * 25 >= data.total} onClick={() => setPage(page + 1)} className="rounded-full border px-4 py-1.5 text-sm disabled:opacity-40" data-testid="admin-communities-next">Next</button>
      </div>
      {edit && <EditDialog c={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); refresh(); }} />}
    </div>
  );
};
